/**
 * Centralised form validators used by NewWork + WorkDetail. Each
 * helper takes the raw string value and returns null when ok, or
 * a Spanish error message ready to render under the field.
 *
 * Empty strings are accepted on every helper except the ones
 * named `*Required` — fields are optional by default; the route
 * layer chooses which ones gate save/submit.
 *
 * Why centralise: WorkDetail had `validatePhone` + `validateRequiredTitle`
 * inline, NewWork had nothing, and the post-explore backlog asked
 * for clearer validation across both. Sharing the helpers keeps
 * the two surfaces consistent.
 */

/** Title (required): non-blank after trimming. */
export function validateRequiredTitle(value: string): string | null {
  if (!value.trim()) return 'El título no puede quedar vacío.';
  return null;
}

/**
 * Phone (optional): only digits / spaces / + / - / ( ) / dot.
 * Mobile accepts whatever the user types; we only reject obvious
 * typos so a paste from contacts still works. Empty is fine.
 */
export function validatePhone(value: string): string | null {
  if (!value.trim()) return null;
  if (!/^[\d\s+()\-.]+$/.test(value)) {
    return 'El teléfono solo admite dígitos, espacios y los signos + - ( ).';
  }
  return null;
}

/**
 * NIF / NIE / CIF (optional): structural check, no checksum
 * verification (avoids importing a Spanish-tax-id lib for a
 * field that's not legally binding here — Verifactu has its own
 * stricter validation in the invoicing layer).
 *
 *   NIF:  8 digits + 1 letter        (e.g. 12345678Z)
 *   NIE:  X|Y|Z + 7 digits + 1 letter (e.g. X1234567Z)
 *   CIF:  letter A-W + 7 digits + letter|digit (e.g. B12345678)
 *
 * Empty is fine. Lowercase accepted (we normalise before checking).
 */
/**
 *   NIF:  8 digits + letter A-Z          (e.g. 12345678Z)
 *   NIE:  X|Y|Z + 7 digits + letter A-Z  (e.g. X1234567Z)
 *   CIF:  letter A-W + 7 digits + letter A-J or digit  (e.g. B12345678)
 * Each branch carries its own valid trailing-character class — the
 * NIF/NIE one is wider (any uppercase letter) than the CIF one
 * (only A-J or a digit, which encodes the org-type checksum
 * alphabet).
 */
const TAX_ID_RE = /^(?:[0-9]{8}[A-Z]|[XYZ][0-9]{7}[A-Z]|[A-W][0-9]{7}[A-J0-9])$/;
export function validateTaxId(value: string): string | null {
  const normalised = value.replace(/\s+/g, '').toUpperCase();
  if (!normalised) return null;
  if (!TAX_ID_RE.test(normalised)) {
    return 'Formato no válido. Ej: 12345678Z, X1234567Z o B12345678.';
  }
  return null;
}

/** Email (optional). Loose but enough to catch typos. Empty is fine. */
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export function validateEmail(value: string): string | null {
  if (!value.trim()) return null;
  if (!EMAIL_RE.test(value.trim())) {
    return 'Formato de email no válido.';
  }
  return null;
}
